import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
  amountFormatter,
  AppTableCellType,
  BaseProductCategory,
  Column,
  getSelectedWalletSection,
  ManagedMacroProduct,
  ManagedProductCategory,
  RealEconomyMacroProduct,
  RealEconomyProductCategory,
  myWalletSliceActions,
  Product,
  ProductCategory,
  ProductMacroCategoryType,
  retrieveColumnsForFinancialPositions,
  sharedAccountsSliceActions,
  useAppDispatch,
  useAppSelector,
  usePirBoxData,
  usePirBoxPageLogic,
  walletFinancialPositionsMessages,
  WelfareProductCategory,
  WelfareProduct
} from 'azshared';
import React, { Fragment, MouseEvent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AppTableCell } from '../../../../../components/table/table-cell';
import { AppTableCellHeader } from '../../../../../components/table/table-cell-header';
import { ManagedTable } from '../managed-table/managed-table';
import './financial-positions-category-table.scss';
import { RealEconomyTable } from '../real-economy-table/real-economy-table';

interface FinancialPositionCategoryTableProps {
  productCategory: ProductCategory;
  macroCategoryType: ProductMacroCategoryType;
}

export const FinancialPositionCategoryTable = (props: FinancialPositionCategoryTableProps) => {
  const { productCategory, macroCategoryType } = props;
  const { productsSummary, positions } = usePirBoxPageLogic();
  const { hasAlternative, hasOrdinary } = usePirBoxData();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const columns: Column<Product>[] = retrieveColumnsForFinancialPositions(macroCategoryType, productCategory?.type);

  const selectedWalletSection = useAppSelector(getSelectedWalletSection);

  const actions = useMemo(() => (selectedWalletSection === 'MY_WALLET' ? myWalletSliceActions : sharedAccountsSliceActions), [selectedWalletSection]);

  const getProduct = useCallback(
    (productCode: string) => {
      let product: Product = null as unknown as Product;
      if (macroCategoryType === 'Managed') {
        (productCategory as ManagedProductCategory).macroProducts?.some((macroProd: ManagedMacroProduct) => {
          product = macroProd.products.find((prod) => prod.productCode === productCode) as Product;

          return !!product;
        });

        return product;
      }
      if (macroCategoryType === 'RealEconomy') {
        (productCategory as RealEconomyProductCategory).macroProducts?.some((macroProd: RealEconomyMacroProduct) => {
          product = macroProd.products.find((prod) => prod.productCode === productCode) as Product;

          return !!product;
        });

        return product;
      }
      return (productCategory as BaseProductCategory).products.find((prod) => prod.productCode === productCode) as Product;
    },
    [productCategory, macroCategoryType]
  );

  const getHasItemInTransit = (category: ProductCategory, macroCategoryType: ProductMacroCategoryType) => {
    if (macroCategoryType === 'Welfare') {
      return (category as WelfareProductCategory).products?.some((product) => product.itemsInTransit !== undefined);
    }
    return false;
  };

  const hasItemsInTransit = useMemo(() => getHasItemInTransit(productCategory, macroCategoryType), [productCategory, macroCategoryType]);

  const isDigitechProduct = useCallback((product: Product) => {
    return product.productName === 'AZIMUT DIGITECH FUND CL.A';
  }, []);

  const pirDeposit = productsSummary.flatMap((summary) => {
    const details = positions.get(summary.pirCode);
    if (details) {
      return details.map((detail) => ({
        ...detail,
        pirType: summary.pirType
      }));
    }
    return [];
  });

  const pirBoxProducts = pirDeposit;
  /*TODO: TEST ABOVE */

  const handleClick = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      const productCode = (e.currentTarget as HTMLSpanElement).id;
      if (productCode && !['Liquidity', 'TraditionalInsurance', '', null, undefined].includes(macroCategoryType)) {
        const product = getProduct(productCode);
        if (product) {
          dispatch(actions.setSelectedProduct(product));
        }
      }
    },
    [dispatch, actions, getProduct]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const productCode = (e.target as HTMLSpanElement).id;
        if (productCode) {
          const product = getProduct(productCode);
          if (product) {
            dispatch(actions.setSelectedProduct(product));
          }
        }
      }
    },
    [dispatch, actions, getProduct]
  );

  const hasExGarantito = useMemo(() => {
    return (productCategory as BaseProductCategory)?.products?.find((p) => ['3411E', '3412E'].includes(p.isin)) !== undefined;
  }, [productCategory]);

  const getPaperContractCode = useCallback((v: string) => {
    const gruppi = v?.split('-');
    if (gruppi?.length > 3) {
      return gruppi.slice(3).join('-');
    }
    return v;
  }, []);

  if (macroCategoryType === 'Managed') {
    const managedCategory = productCategory as ManagedProductCategory;
    const managedCategoryWithPirInfo = {
      ...managedCategory,
      macroProducts: managedCategory.macroProducts.map((macroProduct) => {
        return {
          ...macroProduct,
          products: macroProduct.products.map((p) => {
            const pirProduct = pirBoxProducts?.find((pirP) => pirP.codMandato === p.contract);
            return {
              ...p,
              pirType: pirProduct?.pirType,
              contractIsPir: !!pirProduct
            };
          })
        };
      })
    };

    return <ManagedTable productCategory={managedCategoryWithPirInfo as any} onSelectedProduct={handleClick} />;
  }
  if (macroCategoryType === 'RealEconomy') {
    const realEconomyCategory = productCategory as RealEconomyProductCategory;
    const realEconomyCategoryWithPirInfo = {
      ...realEconomyCategory,
      macroProducts: realEconomyCategory.macroProducts.map((macroProduct) => {
        return {
          ...macroProduct,
          products: macroProduct.products.map((p) => {
            const pirProduct = pirBoxProducts?.find((pirP) => pirP.codMandato === p.contract);
            return {
              ...p,
              pirType: pirProduct?.pirType,
              contractIsPir: !!pirProduct
            };
          })
        };
      })
    };

    return <RealEconomyTable productCategory={realEconomyCategoryWithPirInfo as any} onSelectedProduct={handleClick} />;
  }

  const baseProduct = productCategory as BaseProductCategory;
  const productsWithPirInfo = baseProduct.products.map((prod) => {
    const pirProduct = pirBoxProducts?.find((p) => p.codMandato === prod.contract);
    if (pirProduct) {
      return {
        ...prod,
        contractIsPir: true,
        pirType: pirProduct.pirType
      };
    }
    return prod;
  });

  const hasBothPir = hasAlternative && hasOrdinary;

  return (
    <Fragment>
      <TableContainer className='category-table'>
        <Table aria-label='table'>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => {
                const addStar = hasItemsInTransit && (column.key as keyof WelfareProduct) === 'itemsInTransit';
                return (
                  <AppTableCellHeader key={index} className='category-table-header'>
                    {formatMessage(column.headerLabel) + (addStar ? ' *' : '')}
                  </AppTableCellHeader>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {productsWithPirInfo.map((product, index) => (
              <TableRow key={index} className='category-table-row'>
                {columns.map((col) => (
                  <Fragment key={col.key}>
                    {col.key === 'productName' && (
                      <AppTableCell key={col.key} align='left' type={col.type}>
                        <span
                          id={product.productCode}
                          className={`${
                            macroCategoryType === 'TraditionalInsurance' || macroCategoryType === 'Liquidity' ? '' : 'category-table-row-cell-product-name'
                          }`}
                          onClick={handleClick}
                          onKeyDown={handleKeyDown}
                          tabIndex={0}>
                          {product[col.key]}
                          {['3411E', '3412E'].includes(product.isin) && '**'}
                          {product.contractIsPir && (hasBothPir ? (product.pirType === 'Alternative' ? '*' : '****') : '*')}
                          {product.rp === '0812A' && '****'}
                        </span>
                      </AppTableCell>
                    )}
                    {col.key === 'code' && (
                      <AppTableCell key={col.key} align='left' type={col.type}>
                        {product.rp}
                      </AppTableCell>
                    )}
                    {col.key === 'contract' && (
                      <AppTableCell key={col.key} align='left' type={col.type}>
                        {getPaperContractCode(product[col.key])}
                      </AppTableCell>
                    )}
                    {col.key !== 'productName' &&
                      col.key !== 'code' &&
                      col.key !== 'contract' &&
                      (() => {
                        const shouldShowND = ['obligation', 'date'].includes(col.key as string) && isDigitechProduct(product);

                        return (
                          <AppTableCell key={col.key} align='left' type={(shouldShowND ? 'default' : col.type) as AppTableCellType}>
                            {shouldShowND ? 'N/D' : product[col.key]}
                          </AppTableCell>
                        );
                      })()}
                  </Fragment>
                ))}
              </TableRow>
            ))}
            {macroCategoryType === 'Liquidity' && (
              <TableRow className='category-table-total-row'>
                <td align='left'>Totale</td>
                <td></td>
                {columns.map((column) => {
                  if (column.key === 'accountBalance') {
                    return (
                      <>
                        <AppTableCell key={column.key} type={column.type} align='left'>
                          {productCategory?.totaleSaldoContabile as any}
                        </AppTableCell>
                        <td></td>
                      </>
                    );
                  }

                  if (column.key === 'cashBalance') {
                    return (
                      <>
                        <AppTableCell key={column.key} type={column.type} align='left'>
                          {productCategory?.totaleSaldoLiquido as any}
                        </AppTableCell>
                        <td></td>
                      </>
                    );
                  }
                  if (column.key === 'availableBalance') {
                    return (
                      <AppTableCell key={column.key} type={column.type} align='left'>
                        {productCategory?.totaleSaldoDisponibile as any}
                      </AppTableCell>
                    );
                  }
                })}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {macroCategoryType !== 'Liquidity' && (
        <div className='category-table-footer'>
          <p className='category-table-footer-total-amount'>
            <span>{formatMessage(walletFinancialPositionsMessages.footerTotalLabel)}</span>
            {amountFormatter(productCategory?.totalAmount, { currencyDisplay: 'code' })}
          </p>
          {macroCategoryType === 'Welfare' && hasItemsInTransit && (
            <p className='category-table-footer-disclaimer'>{formatMessage(walletFinancialPositionsMessages.welfareCategoryFooterDisclaimer)}</p>
          )}
          {macroCategoryType === 'Welfare' && hasExGarantito && (
            <p className='category-table-footer-disclaimer'>{formatMessage(walletFinancialPositionsMessages.welfareCategoryFooterExGarantitoDisclaimer)}</p>
          )}
        </div>
      )}
    </Fragment>
  );
};
